import React from 'react';
import styled from 'styled-components';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { ReportAnswers } from '../FeedbackReport';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import {
  NARRATIVE_LESSON_QUESTION_ID,
  NARRATIVE_STORY_QUESTION_ID,
  parseLessonValue,
  parseStoryValue,
} from 'npo/pages/ReportBuilder/Components/Narrative/utils';
import { Report as ReportType } from '../../../DonorReceipt/utils';
import { useReport } from 'shared/providers/ReportProvider';
import NarrativeFile, { RenderFile } from './NarrativeFile';

interface ReportNarrativeProps {
  answers: ReportAnswers;
  isLegacy?: boolean;
  report?: ReportType;
}

const StyledDiv = styled.div`
  margin-top: ${SPACERS.large};
  margin-bottom: ${SPACERS.large};
  border-top: 1px solid ${COLORS.grayLight};
  border-bottom: 1px solid ${COLORS.grayLight};
  background-color: ${COLORS.whiteBase};
  padding: ${SPACERS.large} ${SPACERS.extraLarge};
  .report-narratives {
    display: flex;
    .report-success-story,
    .report-lessons-learned {
      flex: 1;
    }
    .report-success-story {
      padding-right: ${SPACERS.large};
      border-right: 1px solid ${COLORS.grayLight};
      .success-story-title {
        margin-bottom: ${SPACERS.tiny};
      }
      .success-story-image {
        max-width: 100%;
        margin-bottom: ${SPACERS.tiny};
      }
    }
    .report-lessons-learned {
      padding-left: ${SPACERS.large};
      .lessons-learned-title {
        margin-bottom: ${SPACERS.small};
      }
    }
  }
`;

export const renderTextWithParagraphs = (text: string | undefined) => {
  if (!text) return '';

  const paragraphs = text.split(/\r?\n+/g).filter(p => p.length > 0);

  return paragraphs.map((paragraph, paragraphIndex) => (
    <React.Fragment key={paragraphIndex}>
      {paragraph}
      {paragraphIndex < paragraphs.length - 1 ? (
        <div style={{ height: SPACERS.tiny }} />
      ) : null}
    </React.Fragment>
  ));
};

const ReportNarrative: React.FC<ReportNarrativeProps> = ({
  answers,
  isLegacy: legacy,
}) => {
  const { report, isLegacyReport } = useReport();
  const isLegacy = legacy || isLegacyReport;
  const storyFileId = report?.data?.storyFileId;

  // V4 values from answers.narrative
  const narrativeSuccess = answers.narrative?.find(
    x => x.question.dbId === NARRATIVE_STORY_QUESTION_ID,
  )?.text;

  const narrativeLessons = answers.narrative?.find(
    x => x.question.dbId === NARRATIVE_LESSON_QUESTION_ID,
  )?.text;

  // For legacy reports, use plain text values
  const legacySuccessStory = report?.data?.successStory || undefined;
  const legacyLessonLearned = report?.data?.lessonLearned || undefined;

  // Determine which values to use based on isLegacy flag
  let finalSuccessText: string | undefined;
  let finalLessonsText: string | undefined;
  let isUsingLegacyFormat = false;

  if (isLegacy && (legacySuccessStory || legacyLessonLearned)) {
    // For legacy reports: use v3 plain text values
    finalSuccessText = legacySuccessStory;
    finalLessonsText = legacyLessonLearned;
    isUsingLegacyFormat = true;
  } else {
    // For v4 reports or legacy without v3 data: use v4 values
    finalSuccessText = narrativeSuccess;
    finalLessonsText = narrativeLessons;
    isUsingLegacyFormat = false;
  }

  if (!finalSuccessText && !finalLessonsText) return null;

  // Only parse if using v4 format
  const lessons = isUsingLegacyFormat
    ? null
    : parseLessonValue(finalLessonsText);
  const successStory = isUsingLegacyFormat
    ? null
    : parseStoryValue(finalSuccessText);

  return (
    <StyledDiv>
      <TrueImpactText variant="heading-subheading">Narratives</TrueImpactText>
      <div className="report-narratives">
        {finalSuccessText && (
          <div className="report-success-story">
            <TrueImpactText color="blackBase" variant="heading-label">
              Success Story
            </TrueImpactText>
            {isUsingLegacyFormat ? (
              <>
                <TrueImpactText variant="body-small">
                  {renderTextWithParagraphs(finalSuccessText)}
                </TrueImpactText>
                <NarrativeFile fileId={storyFileId} />
              </>
            ) : (
              <>
                <RenderFile
                  file={successStory?.storyFileUrl}
                  title={successStory?.storyTitle}
                />
                <TrueImpactText
                  className="success-story-title"
                  variant="body-bold"
                >
                  {successStory?.storyTitle || 'Unknown'}
                </TrueImpactText>
                <TrueImpactText variant="body-small">
                  {renderTextWithParagraphs(successStory?.storyDescription) ||
                    'Unknown'}
                </TrueImpactText>
              </>
            )}
          </div>
        )}
        {finalLessonsText && (
          <div className="report-lessons-learned">
            <TrueImpactText color="blackBase" variant="heading-label">
              Lessons learned
            </TrueImpactText>
            {isUsingLegacyFormat ? (
              <TrueImpactText variant="body-small">
                {renderTextWithParagraphs(finalLessonsText)}
              </TrueImpactText>
            ) : (
              <>
                <TrueImpactText
                  className="lessons-learned-title"
                  variant="body-small"
                >
                  <strong>Type of lesson:</strong>{' '}
                  {lessons?.storyLesson?.join
                    ? lessons.storyLesson.join(', ')
                    : undefined}
                </TrueImpactText>
                <TrueImpactText variant="body-small">
                  {renderTextWithParagraphs(lessons?.lessonDescription)}
                </TrueImpactText>
              </>
            )}
          </div>
        )}
      </div>
    </StyledDiv>
  );
};

export default ReportNarrative;
